<div class="dark:text-white">
  <app-header-title [title]="'store.list.title' | translate" />

  <!-- Error State -->
  <div *ngIf="productsListError" class="mx-4 my-6">
    <div class="flex flex-col items-center justify-center gap-4 p-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl shadow-lg text-center">
      <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <lucide-icon [name]="iconAlertCircle" [size]="32" class="text-red-600 dark:text-red-400"></lucide-icon>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">{{ 'store.list.error' | translate }}</h3>
        <p class="text-red-700 dark:text-red-300">{{ productsListError }}</p>
      </div>
      <p-button [label]="'store.list.refresh' | translate" icon="pi pi-refresh" (onClick)="loadPage()" [disabled]="productsLoading"></p-button>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!productsListError" class="mx-4 my-6 space-y-6">

    <!-- Credits Section -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Section Header -->
      <div class="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/20 px-6 py-5 border-b border-primary-200 dark:border-primary-800">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-primary-600 dark:bg-primary-500 flex items-center justify-center shadow-md">
            <lucide-icon [name]="iconCoins" [size]="24" class="text-white"></lucide-icon>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ 'store.list.credits.title' | translate }}</h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ 'store.list.credits.subtitle' | translate }}</p>
          </div>
        </div>
      </div>

      <!-- Section Content -->
      <div class="p-6">
        <p class="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
          {{ 'store.list.credits.description' | translate }}
        </p>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Product Card -->
          <div>
            <app-shop-package-item [canAdminAdd]="true" [product]="actualPackageProduct" [companyProfile]="companyProfile" [disableBuy]="disableBuy"></app-shop-package-item>
          </div>

          <!-- Configurator -->
          <div class="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-2 mb-4">
              <lucide-icon [name]="iconSettings" [size]="20" class="text-primary-600 dark:text-primary-400"></lucide-icon>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ 'store.list.configurator.title' | translate }}</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">{{ 'store.list.configurator.description' | translate }}</p>

            <!-- Duration Selection -->
            <div class="mb-6">
              <label for="duration-select" class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <lucide-icon [name]="iconCalendar" [size]="16" class="text-primary-600 dark:text-primary-400"></lucide-icon>
                {{ 'store.list.configurator.duration' | translate }}
              </label>
              <p-selectbutton id="duration-select" [options]="durations" [(ngModel)]="selectedDuration" [multiple]="false" (onChange)="onChangeDuration($event)"></p-selectbutton>
            </div>

            <!-- Cards Count Selection -->
            <div class="mb-6">
              <label for="cards-count-select" class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <lucide-icon [name]="iconCreditCard" [size]="16" class="text-primary-600 dark:text-primary-400"></lucide-icon>
                {{ 'store.list.configurator.cardsCount' | translate }}
              </label>
              <p-selectbutton id="cards-count-select" [options]="cardsCount" [(ngModel)]="selectedCardsCount" [multiple]="false" (onChange)="onChangeCardsCount($event)" [disabled]="customCountAdminEnabled"></p-selectbutton>
            </div>

            <!-- Admin Custom Count -->
            <div *ngIf="isAdmin()" class="space-y-4">
              <div class="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p-toggleswitch [(ngModel)]="customCountAdminEnabled" inputId="customCountAdmin" (ngModelChange)="onCustomCountAdminEnabledChange()"></p-toggleswitch>
                <label for="customCountAdmin" class="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                  <fa-icon [icon]="adminIcon" class="text-amber-600 dark:text-amber-400"></fa-icon>
                  {{ 'store.list.configurator.customValue' | translate }}
                </label>
              </div>
              <div *ngIf="customCountAdminEnabled" class="pl-3">
                <p-inputNumber [(ngModel)]="customCount" [min]="1" [max]="1000" [showButtons]="true" [buttonLayout]="'horizontal'" (ngModelChange)="onCustomCountChange($event)" styleClass="w-full"></p-inputNumber>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Section -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Section Header -->
      <div class="bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-800/20 px-6 py-5 border-b border-green-200 dark:border-green-800">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-green-600 dark:bg-green-500 flex items-center justify-center shadow-md">
            <lucide-icon [name]="iconShoppingBag" [size]="24" class="text-white"></lucide-icon>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ 'store.list.services.title' | translate }}</h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ 'store.list.services.subtitle' | translate }}</p>
          </div>
        </div>
      </div>

      <!-- Section Content -->
      <div class="p-6">
        <p class="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
          {{ 'store.list.services.description' | translate }}
        </p>

        <p-carousel [value]="filteredSubscriptions" [numVisible]="3" [numScroll]="3" [circular]="true" [responsiveOptions]="carouselResponsiveOptions">
          <ng-template let-product pTemplate="item">
            <div class="px-3">
              <app-shop-package-item [product]="product" [companyProfile]="companyProfile"></app-shop-package-item>
            </div>
          </ng-template>
        </p-carousel>
      </div>
    </div>

  </div>
</div>
